/* eslint-disable no-param-reassign */
export default class WorldMapChestDynamicLayer extends Phaser.Group {
  constructor(map, editorMode) {
    super(game);

    this.position = map.position;
    this.map = map;
    this.editorMode = editorMode;

    this.freeInstances = [];

    if (this.editorMode) {
      this._initEditor(this.map);
    } else {
      this._init(this.map);
    }

    G.sb('onWindowOpened').add(this.lockInput, this);
    G.sb('onWindowClosed').add(this.unlockInput, this);

    this.signalBinding = G.Helpers.gatesMgr.signals.onGateOpened.add(this.generateNewChests, this);
  }

  destroy() {
    this.freeInstances.forEach((child) => {
      child.destroy();
    });
    if (this.signalBinding && this.signalBinding._listener && this.signalBinding.context) {
      G.Helpers.gatesMgr.signals.onGateOpened.remove(this.signalBinding._listener, this.signalBinding.context);
    }
  }

  _init(map) {
    // imports
    this.activeChests = G.saveState.mapChestDataManager.getChestArray(true);
    map.signals.onMapScrolled.add(G.Helpers.callOnThreshold(100, this.checkInstancesPlacements, this));
  }

  _initEditor(map) { // eslint-disable-line no-unused-vars
    this.update = () => {};
  }

  generateNewChests(gateId) {
    const newChests = G.saveState.mapChestDataManager.generateNewChestsToNewGate(gateId, this.map.getSingleTileMapHeight());
    this.activeChests = this.activeChests.concat(newChests);
    this.checkInstancesPlacements();
  }

  unlockInput() {
    this.ignoreChildInput = false;
    this.children.forEach((child) => { child.ignoreChildInput = false; });
  }

  lockInput() {
    this.ignoreChildInput = true;
    this.children.forEach((child) => { child.ignoreChildInput = true; });
  }

  checkInstancesPlacements() {
    for (let i = 0, len = this.activeChests.length; i < len; i++) {
      const chestData = this.activeChests[i];
      const wy = this.y + chestData.y;

      if ((wy > -200 && wy < game.height + 200) && !chestData.o) {
        if (!chestData.instance) {
          this.placeInstance(chestData);
        }
      } else if (chestData.instance) {
        this.hideInstance(chestData);
      }
    }
  }

  placeInstance(chestData) {
    chestData.instance = this.getFreeInstance();
    chestData.instance.init(chestData);
    this.add(chestData.instance);
  }

  hideInstance(chestData) {
    chestData.instance.hide();
    chestData.instance.parent.removeChild(chestData.instance);
    this.freeInstances.push(chestData.instance);
    chestData.instance = null;
  }

  getFreeInstance() {
    // use pooled instance
    if (this.freeInstances.length > 0) {
      return this.freeInstances.shift();
    }
    // create new instance
    const mapScale = G.OMTsettings.elements.worldMap && G.OMTsettings.elements.worldMap.mapScale ? G.OMTsettings.elements.worldMap.mapScale : 1;
    const instance = new ChestInstance(this.editorMode); // eslint-disable-line no-use-before-define
    instance.scale.x *= mapScale;
    instance.scale.y *= mapScale;
    return instance;
  }
}

//
export class ChestInstance extends Phaser.Group {
  constructor(editorMode) {
    super(game);

    this.state = game.state.getCurrentState();

    this.editorMode = editorMode;

    this.shadow = G.makeImage(0, 40, 'chest_shadow', 0.5, this);
    this.chest = new G.Button(0, 0, 'chest', this.onClick, this);
    this.chest.IMMEDIATE = true;
    this.chestGlow = G.makeImage(0, 0, 'chest', 0.5, this.chest);
    this.chestGlow.blendMode = 1;
    this.chestGlow.alpha = 0.4;
    game.add.tween(this.chestGlow).to({ alpha: 0 }, 500, Phaser.Easing.Sinusoidal.InOut, true, 0, -1, true);

    this.add(this.chest);

    if (!this.editorMode) {
      game.time.events.loop(2000, this.setJumpRepeat, this);
    }
  }

  init(chestData) {
    G.changeTexture(this.chest, 'chest');
    G.changeTexture(this.chestGlow, 'chest');
    this.chestData = chestData;
    this.position.setTo(chestData.x, chestData.y);
    this.orgX = this.x;
    this.orgY = this.y;

    this.chest.inputEnabled = true;
    this.chest.input.useHandCursor = true;

    this.chest.scale.x = this.x < 0 ? -1.5 : 1.5;
    this.chest.scale.y = 1.5;
    this.shadow.scale.x = this.chest.scale.x;
    this.alpha = 1;

    this.opened = false;

    this.id = chestData.id;
    this.currentStars = G.saveState.getAllStars();
    this.unlocked = this.currentStars >= chestData.sr;
    this.starsRequirement = chestData.sr;

    if (this.editorMode) {
      return this._initEditor(chestData);
    }

    // scaling button on click;

    if (this.unlocked) {
      this.chestGlow.visible = true;
      this.setJumpRepeat();
    } else {
      this.chestGlow.visible = false;

      this.label = new G.LabelTextT(
        `${Math.min(this.currentStars, this.starsRequirement)}/${this.starsRequirement}@map_star_1@`,
        0, 50, {
          style: 'font-white',
          fontSize: '40px',
        }, 0.5, 150,
      );
      this.add(this.label);
    }
    return null;
  }

  _initEditor(chestData) { // eslint-disable-line no-unused-vars
    const style = {
      style: 'font-white',
      fontSize: '30px',
    };

    const addLabelBg = (label) => {
      const labelBg = this.add(G.Helpers.createRectangleGraphics({
        width: label.width,
        height: label.height,
        color: 0x000000,
        alpha: 0.5,
        anchor: 0.5,
      }));
      labelBg.position = label.position;
      this.setChildIndex(labelBg, this.getChildIndex(label));
    };

    this.idLabel = this.add(new G.Text(0, -40, `id: ${this.id}`, style, 0.5));
    addLabelBg(this.idLabel);

    this.label = this.add(new G.LabelTextT(
      `${this.starsRequirement}@map_star_1@`,
      0, 30, style, 0.5, 200,
    ));
    addLabelBg(this.label);

    /*
    this.giftLabel = this.add(new G.LabelTextT(
      G.gift.getLabelString(mapChestsMgr.getChestGifts(this.chestData.id)),
      0, -10, style, 0.5, 200));
    addLabelBg(this.giftLabel);
    */

    this.chestGlow.visible = false;
    this.chest.inputEnabled = false;
  }

  update() {
    this.shadow.alpha = 1 + ((this.chest.y / 150));
  }

  hide() {
    G.stopTweens(this.chest);
    this.chest.position.setTo(0, 0);
    this.chest.angle = 0;
    if (this.label) this.label.destroy();
  }

  onClick() {
    // imports
    const { mapChestsMgr } = G.Helpers;

    if (this.unlocked) {
      G.changeTexture(this.chest, 'chest_open');
      G.changeTexture(this.chestGlow, 'chest_open');

      this.opened = true;
      this.chestData.o = true;

      const chestGifts = G.saveState.mapChestDataManager.openChest(this.chestData.i);

      this.chest.inputEnabled = false;

      G.sb('pushWindow').dispatch(['gift', {
        showTitleBar: false,
        showGingy: false,
        showGingyGiftBar: false,
        autoOpen: true,
        autoOpenDelay: 700,
        reason: G.GiftUI.Constants.GiftReasons.Chest,
        shareable: false,
        gifts: chestGifts,
        monetiseType: false,
        onlyOneBox: true,
        onClaim: () => {
          G.sb('mapChestWindowClosed').dispatch();
          if (mapChestsMgr.showBotOptinIconAfter === 0) {
            G.saveState.data.showOptinIcon = true;
          }
          if (mapChestsMgr.showBotOptinPopupAfter === 0) {
            G.sb('pushWindow').dispatch(['botOptIn', () => {}, this]);
          }
          mapChestsMgr.showBotOptinIconAfter--;
          mapChestsMgr.showBotOptinPopupAfter--;

          // if (!G.saveState.data.isHomeScreenIconAdded && !G.saveState.data.isHomeScreenPopupSeen) {
          //   OMT.platformFunctions.canCreateShortcutAsync()
          //     .then((canCreate) => {
          //       if (canCreate) {
          //         G.sb('pushWindow').dispatch('newsHomeScreen');
          //         G.saveState.data.isHomeScreenPopupSeen = true;
          //       }
          //     })
          //     .catch((e) => {
          //       console.log('social.createShortcut', e);
          //     });
          // }
        },
        resourceTracking: {
          itemType: 'Reward',
          itemId: 'MapChest',
        },
      }], false, G.WindowMgr.LayerNames.BelowHeaderPanel);

      game.add.tween(this).to({ alpha: 0 }, 500, Phaser.Easing.Sinusoidal.InOut, true, 1000).onComplete.add(() => {
        this.hide();
      });
    } else {
      this.chest.inputEnabled = false;
      this.jump(() => {
        this.chest.inputEnabled = true;
        this.chest.input.useHandCursor = true;
      });
    }
  }

  setJumpRepeat(callback, context) { // eslint-disable-line no-unused-vars
    if (this.opened) {
      return;
    }
    if (!this.unlocked) {
      return;
    }
    if (!this.parent) {
      return;
    }
    this.jump();
  }


  jump(callback, context) {
    const moveTweenA = game.add.tween(this.chest).to({ y: -G.l(150) }, 300, Phaser.Easing.Cubic.Out);
    const moveTweenB = game.add.tween(this.chest).to({ y: 0 }, 300, Phaser.Easing.Circular.In);
    moveTweenA.chain(moveTweenB);
    moveTweenA.start();

    const tweenAngleA = game.add.tween(this.chest).to({ angle: -15 }, 200, Phaser.Easing.Cubic.InOut);
    const tweenAngleB = game.add.tween(this.chest).to({ angle: 15 }, 375, Phaser.Easing.Sinusoidal.In);
    const tweenAngleC = game.add.tween(this.chest).to({ angle: 0 }, 50, Phaser.Easing.Cubic.InOut);

    tweenAngleA.chain(tweenAngleB, tweenAngleC);
    tweenAngleA.start();

    if (callback) {
      tweenAngleC.onComplete.add(callback, context);
    }
  }
}
